// src/Components/HighLevelComponents/ChatToolCreator/ChatToolCreator.tsx
import React, { useState, useRef, useEffect, useContext } from "react";
import {
  FaPaperPlane,
  FaSpinner,
  FaRobot,
  FaUser,
  FaArrowLeft,
} from "react-icons/fa";
import { GoogleAnalyticsContext } from "../../../Hooks/GoogleAnalyticsContext";
import { useAuthenticatedFetchV2 } from "../../../Hooks/useAuthenticatedFetchV2";
import config from "../../../config";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
  id: string;
  role: "system" | "user" | "assistant" | "function";
  content: string;
  createdAt: Date;
}

interface InitSessionResponse {
  sessionId: string;
  message: {
    content: string;
  };
}

interface SendMessageResponse {
  sessionId: string;
  message: {
    content: string;
  };
  functionCalled: boolean;
  functionResult?: {
    success: boolean;
    promptId: string;
  };
}

const ChatToolCreator: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);

  // API hooks
  const { callAPI: initSession, isLoading: isInitializing } =
    useAuthenticatedFetchV2<InitSessionResponse>({
      url: `${config.API_URI}/api/chat-tool-creator/init`,
      method: "POST",
    });

  const { callAPI: sendMessage, isLoading: isSending } =
    useAuthenticatedFetchV2<SendMessageResponse>({
      url: `${config.API_URI}/api/chat-tool-creator/message`,
      method: "POST",
    });

  const { callAPI: endSession } = useAuthenticatedFetchV2({
    url: "", // Will be set dynamically
    method: "DELETE",
  });

  // Initialize chat session on component mount
  useEffect(() => {
    async function initializeChat() {
      try {
        const response = await initSession();
        if (response.data?.sessionId && response.data?.message) {
          setSessionId(response.data.sessionId);

          // Add initial greeting
          setMessages([
            {
              id: "0",
              role: "assistant",
              content: response.data.message.content,
              createdAt: new Date(),
            },
          ]);

          logEvent("ChatToolCreator", "InitSession", "success");
        } else if (response.error) {
          console.error("Error initializing chat:", response.error);
          logEvent("ChatToolCreator", "InitSession", "error");
          toast.error("Failed to initialize chat. Please try again later.");
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        logEvent("ChatToolCreator", "InitSession", "error");
        toast.error("Failed to initialize chat. Please try again later.");
      }
    }

    initializeChat();

    // Cleanup function to end the session when component unmounts
    return () => {
      if (sessionId) {
        endSession({
          url: `${config.API_URI}/api/chat-tool-creator/session/${sessionId}`,
        });
      }
    };
  }, []); // Empty dependency array - only run on mount

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!input.trim() || isSending || !sessionId) return;

    const userMessage = input.trim();
    setInput("");

    // Add user message to chat
    const newUserMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, newUserMessage]);

    try {
      const response = await sendMessage({
        sessionId,
        message: userMessage,
      });

      if (response.data?.message) {
        // Add assistant response
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: response.data.message.content,
          createdAt: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Handle function calls (tool creation)
        if (
          response.data?.functionCalled &&
          response.data?.functionResult?.success &&
          response.data?.functionResult?.promptId
        ) {
          console.log(response.data.functionResult);
          const promptId = response.data.functionResult.promptId;
          logEvent("ChatToolCreator", "ToolCreated", `tool_${promptId}`);
          toast.success("Custom tool created successfully!");

          // After a short delay, navigate to the specific tool
          setTimeout(() => {
            navigate(`/custom-tools/${promptId}`);
          }, 3000);
        }
      } else if (response.error) {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      logEvent("ChatToolCreator", "SendMessage", "error");
      toast.error("Failed to process your message. Please try again.");

      // Add error message
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content:
          "I'm sorry, I encountered an error while trying to process your request. Please try again or contact support if the problem persists.",
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  const isProcessing = isInitializing || isSending;

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-md h-[70vh] max-h-[700px]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center bg-gradient-to-r from-yellow-50 to-amber-50">
        <button
          onClick={() => navigate("/custom-tools")}
          className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Chat with Yip</h2>
          <p className="text-sm text-gray-600">
            Let's create your perfect tool together
          </p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-grow overflow-y-auto p-4 bg-gray-50">
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === "user"
                    ? "bg-gray-100 text-gray-800 border border-gray-200"
                    : message.role === "function"
                    ? "bg-purple-100 text-gray-800"
                    : "bg-white text-gray-800 shadow-sm"
                }`}
              >
                <div className="flex items-center mb-2">
                  {message.role === "user" ? (
                    <FaUser className="text-gray-500 mr-2" />
                  ) : (
                    <FaRobot className="text-gray-500 mr-2" />
                  )}
                  <span className="text-xs opacity-75">
                    {message.createdAt.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-800 prose-code:text-gray-700 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:text-gray-800 prose-pre:border prose-pre:border-gray-200 prose-pre:rounded-md prose-pre:p-4 prose-pre:overflow-x-auto prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 prose-li:text-gray-700 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:text-gray-600">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
              disabled={isProcessing}
            />
            <button
              type="submit"
              disabled={isProcessing || !input.trim()}
              className={`p-3 rounded-full transition-all ${
                isProcessing || !input.trim()
                  ? "bg-gray-100 text-gray-400"
                  : "bg-yellow-400 text-gray-800 hover:bg-yellow-500"
              }`}
            >
              {isProcessing ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaPaperPlane />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {isProcessing
              ? "Yip is thinking..."
              : "Press Enter to send your message"}
          </p>
        </form>
      </div>
    </div>
  );
};

export default ChatToolCreator;
